import { toast } from 'sonner';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';
import SignatureCanvas from 'react-signature-canvas';

function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [signatures, setSignatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [signingContract, setSigningContract] = useState(null);
  const signaturePad = useRef(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await api.get('/api/contracts');
      setContracts(response.data);
      
      for (const contract of response.data) {
        try {
          const sigRes = await api.get(`/api/contracts/${contract._id}/signatures`);
          setSignatures(prev => ({ ...prev, [contract._id]: sigRes.data }));
        } catch (err) {
          console.error(`Error fetching signatures:`, err);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (contractId) => {
    if (!signaturePad.current || signaturePad.current.isEmpty()) {
      toast.info(t('common.signHere'));
      return;
    }

    try {
      const signatureData = signaturePad.current.toDataURL();
      await api.post(`/api/contracts/${contractId}/sign`, {
        contract_id: contractId,
        signer: 'lender',
        signature_data: signatureData
      });
      toast.success(t('contracts.signed'));
      setSigningContract(null);
      fetchContracts();
    } catch (error) {
      toast.error(t('common.error') + ': ' + (error.response?.data?.detail || t('common.error')));
    }
  };

  const handleDownload = async (contractId) => {
    try {
      const response = await api.get(`/api/contracts/${contractId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      toast.error('Error al descargar');
    }
  };

  const getSignatureStatus = (contractId) => {
    const sigs = signatures[contractId] || [];
    const hasLender = sigs.some(s => s.signer === 'lender');
    const hasCompany = sigs.some(s => s.signer === 'company');
    
    if (hasLender && hasCompany) return { text: `✓ ${t('contracts.signed')}`, color: 'active' };
    if (hasLender) return { text: t('contracts.signed'), color: 'completed' };
    return { text: t('contracts.pendingSignature'), color: 'pending' };
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('contracts.title')} />

        <main style={{ padding: '1rem' }} className="fade-in">
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            {contracts.map((contract) => {
              const sigStatus = getSignatureStatus(contract._id);
              const contractSigs = signatures[contract._id] || [];
              const hasLenderSigned = contractSigs.some(s => s.signer === 'lender');
              
              return (
                <div key={contract._id} className="fintech-card" data-testid={`contract-${contract._id}`} style={{ padding: '1.25rem' }}>
                  {/* Header */}
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#103766', marginBottom: '0.5rem' }}>
                    Contrato #{contract._id.slice(-8).toUpperCase()}
                  </h3>
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                    {new Date(contract.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <div style={{ marginBottom: '1rem' }}>
                    <span className={`status-badge ${sigStatus.color}`}>{sigStatus.text}</span>
                  </div>
                  
                  {/* Action buttons - stacked for mobile */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: contractSigs.length > 0 ? '1rem' : 0 }}>
                    <button onClick={() => handleDownload(contract._id)} className="btn-secondary" data-testid={`download-contract-${contract._id}`}
                      style={{ padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
                      📥 {t('misc.download')}
                    </button>
                    <button onClick={() => navigate(`/loans/${contract.loan_id}`)} className="btn-secondary"
                      style={{ padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', background: 'rgba(124,58,237,0.15)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.3)' }}>
                      📁 {t('documents.title')}
                    </button>
                    {!hasLenderSigned && contract.status === 'pending_signatures' && (
                      <button onClick={() => setSigningContract(contract)} className="btn-primary" data-testid={`sign-contract-${contract._id}`}
                        style={{ padding: '0.6rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', fontWeight: '700' }}>
                        ✍️ {t('misc.sign')}
                      </button>
                    )}
                  </div>

                  {/* Signature Status */}
                  {contractSigs.length > 0 && (
                    <div style={{
                      marginTop: '1.5rem',
                      padding: '1.25rem',
                      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                      borderRadius: '12px',
                      borderLeft: '4px solid #103766'
                    }}>
                      <p style={{ fontWeight: '600', color: '#103766', marginBottom: '1rem' }}>{t('contracts.signatures')}:</p>
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {contractSigs.map((sig, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>
                              {sig.signer === 'lender' ? '👤' : '🏢'}
                            </span>
                            <div>
                              <p style={{ fontWeight: '600', color: '#103766' }}>
                                {sig.signer === 'lender' ? t('contracts.lender') : t('contracts.company')}
                              </p>
                              <p style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                                {t('date.signedOn')} {new Date(sig.ts_eu_madrid).toLocaleDateString(i18n.language === 'ca' ? 'ca-ES' : 'es-ES')} - {t('date.ip')}: {sig.ip}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {contracts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '6rem 0' }}>
              <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>📄</div>
              <p style={{ fontSize: '1.5rem', color: '#6b7280', fontWeight: '500' }}>{t('contracts.noContracts')}</p>
              <p style={{ fontSize: '1rem', color: '#9ca3af', marginTop: '0.5rem' }}>{t('contracts.contractsWillAppear')}</p>
            </div>
          )}
        </main>
      </div>

      {/* Signature Modal */}
      {signingContract && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setSigningContract(null); }}>
          <div className="modal-content" style={{ maxWidth: '520px', width: '95%', padding: '1.5rem', margin: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#103766', margin: 0 }}>
                {i18n.language === 'ca' ? 'Signar Contracte' : 'Firmar Contrato'}
              </h3>
              <button onClick={() => setSigningContract(null)}
                style={{ background: '#f3f4f6', border: 'none', width: '32px', height: '32px', borderRadius: '50%', fontSize: '1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>
            
            <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.85rem' }}>
              {i18n.language === 'ca' ? 'Signa amb el dit o ratolí al requadre:' : 'Firme con el dedo o ratón en el recuadro:'}
            </p>
            
            <div style={{ marginBottom: '1rem' }}>
              <SignatureCanvas
                ref={signaturePad}
                canvasProps={{
                  style: { width: '100%', height: '200px', border: '2px dashed #d1d5db', borderRadius: '10px', background: '#fafafa' },
                  'data-testid': 'signature-canvas'
                }}
              />
            </div>
            
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem' }}>
              {i18n.language === 'ca' ? 'La seva signatura es registrarà amb IP i timestamp per validesa legal' : 'Su firma se registrará con IP y timestamp para validez legal'}
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={() => signaturePad.current?.clear()} className="btn-secondary" data-testid="clear-signature"
                style={{ padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                {i18n.language === 'ca' ? 'Netejar' : 'Limpiar'}
              </button>
              <div style={{ flex: 1 }}></div>
              <button onClick={() => setSigningContract(null)} className="btn-secondary" data-testid="cancel-signature"
                style={{ padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                {i18n.language === 'ca' ? 'Cancel·lar' : 'Cancelar'}
              </button>
              <button onClick={() => handleSign(signingContract._id)} className="btn-primary" data-testid="submit-signature"
                style={{ padding: '0.6rem 1.25rem', fontSize: '0.85rem', fontWeight: '700' }}>
                ✓ {i18n.language === 'ca' ? 'Signar' : 'Firmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contracts;